import { interpolatingMedian } from "../utils/median.js";
import { bearingDeltaDeg as bearingDeltaDeg$1, normalizeBearingDeg } from "../utils/bearing-degrees.js";
import { registerFrameUpdate } from "./frame-loop.js";
import { registerXrFrameUpdate } from "./xr-frame-loop.js";
import { registerSessionDisposer } from "./session-disposers.js";
import { solarPosition } from "../geo/solar-position.js";
import { nearestRankPercentile } from "../utils/percentile.js";
import { alignmentYawDeg, principalRayCamera, rotateByMat4, sightingErrorDeg, sunDirectionNue } from "./sun-check-geometry.js";
import { createSunMarker } from "./sun-marker.js";
import * as THREE from "three";
import { nueToWebXR } from "gps-plus-slam-js";
//#region ../src/ar/sun-check.ts
/**
* The AR sun check's controller (plan 2026-09-24-0100, M2 and M2b, §3.4):
* keeps the sun marker on the real (apparent) sun, and turns "aim the
* reticle at the sun, press Mark" into a measured heading error.
*
* SAMPLED WHERE IT IS DRAWN. Each sample is taken in the marker's
* `onBeforeRender`, with the camera three renders it with: the ray the user
* SEES and the alignment-free ray come from the same frame by construction,
* whatever order the app's frame callbacks run in (the recorder's sampler
* ran before its alignment lerper; plan review finding 3). ONLY TRACKED
* FRAMES count: when the viewer pose is null (tracking lost) three renders
* the last view again, which would read as a perfectly steady phone
* (M2 review finding 1).
*
* THE MARK. The window is the `holdMs` ending `preGuardMs` BEFORE the press
* (a click fires at touch-up, after the finger has pushed the phone; plan
* review finding 5, M2 review finding 5); when that second is missing or
* the phone moved in it, the `holdMs` starting `joltMs` AFTER the press. The
* result is the median heading and elevation error; the spread is measured
* on the ALIGNMENT-FREE ray, so an alignment update mid-window is flagged,
* not refused as hand shake (review finding 4). A Mark always resolves, as a
* sighting or a refusal: never hangs, never throws.
*
* THE CLOCK is injected (`nowEpochMs`; production passes `Date.now`, never
* the XR clock, which stalls while the phone sleeps; review finding 1), and
* each sighting logs its offset from the monotonic clock.
*
* @see sun-check.ts.md
*/
/** The controller's rules, in one place (overridable through `deps.rules`). */
const SUN_CHECK = {
	/** The Mark window. */
	holdMs: 1e3,
	/** The pre-press window ends this long before the press (the tap's push). */
	preGuardMs: 150,
	/** Past the tap's jolt, when the window has to start after the press. */
	joltMs: 300,
	/** A Mark with fewer frames is refused. */
	minFrames: 10,
	/**
	* Hand shake above this refuses the Mark: the 80th-percentile angular
	* distance of the alignment-free ray from its median direction (four
	* frames in five within it). Not the median distance: a two-cluster shake
	* puts the median direction inside the larger cluster and reads 0. Stricter
	* than the plan's first "median absolute deviation" at the same bound.
	*/
	maxSpreadDeg: .3,
	spreadPercentile: .8,
	/** A drawn-yaw range above this inside the window is flagged. */
	maxYawRangeDeg: .05,
	/** How long a Mark waits for frames past its window before giving up. */
	giveUpMs: 1500,
	/** The marker hides below this apparent elevation. */
	hideBelowDeg: -1,
	/** A Mark is refused below this apparent elevation. */
	minMarkElevationDeg: 0,
	/** Above this, heading resolution degrades (plan §5.1): a warning. */
	highSunDeg: 35,
	/** How often the sun is recomputed. */
	sunUpdateMs: 250,
	/** Samples older than this are dropped. */
	keepMs: 3e3
};
const DEG = Math.PI / 180;
const IDENTITY = new THREE.Matrix4();
const STANDARD_AIR = {
	pressureHPa: 1010,
	temperatureC: 10
};
const normalize = (v) => {
	const l = Math.hypot(v[0], v[1], v[2]);
	return [
		v[0] / l,
		v[1] / l,
		v[2] / l
	];
};
const medianDirection = (rays) => normalize([
	interpolatingMedian(rays.map((r) => r[0])),
	interpolatingMedian(rays.map((r) => r[1])),
	interpolatingMedian(rays.map((r) => r[2]))
]);
const separationDeg = (a, b) => Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]))) / DEG;
/**
* The rules that are elevations and may be negative. Named, not matched by
* suffix: a negative spread or range bound would refuse every Mark silently.
*/
const SIGNED_RULES = new Set(["hideBelowDeg", "minMarkElevationDeg"]);
/** The rules with overrides, each finite, and non-negative unless signed. */
function rulesOf(overrides) {
	const rules = {
		...SUN_CHECK,
		...overrides
	};
	for (const [key, value] of Object.entries(rules)) if (!Number.isFinite(value) || value < 0 && !SIGNED_RULES.has(key)) throw new RangeError(`sun check rule ${key} must be finite${SIGNED_RULES.has(key) ? "" : " and non-negative"}, got ${value}`);
	return rules;
}
/** Runs `f`, or returns `fallback` if it throws (app callbacks, maths). */
function guarded(f, fallback) {
	try {
		return f();
	} catch {
		return fallback;
	}
}
/**
* Starts the sun check: adds the marker and the reticle to `deps.scene`,
* keeps them on the sun, samples every drawn, tracked frame. Disposed with
* the AR session.
*/
function startSunCheck(deps) {
	const rules = rulesOf(deps.rules);
	const marker = createSunMarker();
	deps.scene.add(marker.object, marker.reticle);
	const monotonic = deps.monotonicEpochMs ?? (() => performance.timeOrigin + performance.now());
	const air = {
		...STANDARD_AIR,
		...deps.refraction
	};
	let samples = [];
	let sun = null;
	let hiddenBecause = "no-position";
	let lastSunMs = Number.NEGATIVE_INFINITY;
	let tracked = false;
	let pending = null;
	let disposed = false;
	const aligned = () => !deps.arWorldGroup.matrix.equals(IDENTITY);
	const sunAt = (t, place) => {
		const apparent = solarPosition(t, place.lat, place.lng, { refraction: air });
		const geometric = solarPosition(t, place.lat, place.lng);
		return {
			azDeg: apparent.azimuthRad / DEG,
			elDeg: apparent.elevationRad / DEG,
			refractionDeg: (apparent.elevationRad - geometric.elevationRad) / DEG,
			dir: sunDirectionNue(apparent.azimuthRad, apparent.elevationRad)
		};
	};
	const computeSun = () => {
		const place = guarded(() => deps.getZeroReference(), null);
		if (place === null) return null;
		return guarded(() => ({
			...sunAt(deps.nowEpochMs(), place),
			place
		}), null);
	};
	/** Recompute the sun and the marker's visibility; returns why it is hidden. */
	const refresh = () => {
		sun = computeSun();
		lastSunMs = deps.nowEpochMs();
		if (sun === null) hiddenBecause = "no-position";
		else if (!aligned()) hiddenBecause = "no-alignment";
		else if (!tracked) hiddenBecause = "not-tracking";
		else if (sun.elDeg < rules.hideBelowDeg) hiddenBecause = "sun-down";
		else hiddenBecause = void 0;
		if (sun !== null) marker.setSun(sun.azDeg, sun.elDeg);
		marker.setVisible(hiddenBecause === void 0);
		return hiddenBecause;
	};
	const unregisterFrame = registerFrameUpdate(() => {
		if (Math.abs(deps.nowEpochMs() - lastSunMs) >= rules.sunUpdateMs) refresh();
	});
	const unregisterXr = registerXrFrameUpdate(({ frame, referenceSpace }) => {
		const pose = guarded(() => frame.getViewerPose(referenceSpace), null);
		const now = pose !== null && pose !== void 0 && !pose.emulatedPosition;
		if (now !== tracked) {
			tracked = now;
			refresh();
		}
	});
	const inverse = new THREE.Matrix4();
	const odoCamera = new THREE.Matrix4();
	const viewport = new THREE.Vector4();
	marker.object.onBeforeRender = (renderer, _scene, camera) => {
		if (disposed || sun === null || !tracked) return;
		const xr = renderer?.xr;
		if (xr?.isPresenting === true) {
			const views = xr.getCamera().cameras;
			if (views.length > 1 && camera !== views[0]) return;
		}
		guarded(() => {
			sample(camera, renderer);
			return true;
		}, false);
	};
	const sample = (camera, renderer) => {
		const place = sun.place;
		const world = deps.arWorldGroup.matrixWorld;
		const yaw = alignmentYawDeg(world.elements);
		const projection = [...camera.projectionMatrix.elements];
		const ray = principalRayCamera(projection);
		const rGps = normalize(rotateByMat4(camera.matrixWorld.elements, ray));
		inverse.copy(world).invert();
		const rOdo = normalize(rotateByMat4(inverse.elements, rGps));
		odoCamera.multiplyMatrices(inverse, camera.matrixWorld);
		const viewQ = new THREE.Quaternion().setFromRotationMatrix(odoCamera);
		const own = camera.viewport;
		if (own !== void 0) viewport.copy(own);
		else if (renderer !== null) renderer.getViewport(viewport);
		else viewport.set(0, 0, 0, 0);
		const t = deps.nowEpochMs();
		samples.push({
			t,
			rGps,
			rOdo,
			sun: sunAt(t, place),
			yaw,
			target: guarded(() => deps.getTargetYawDeg?.() ?? null, null),
			viewQ,
			projection,
			viewportW: viewport.z,
			viewportH: viewport.w
		});
		samples = samples.filter((s) => Math.abs(t - s.t) <= rules.keepMs);
		if (pending !== null && t >= pending.end) finish(pending.start, pending.end);
	};
	const aggregate = (start, end) => {
		const window = samples.filter((s) => s.t >= start && s.t <= end);
		if (window.length < rules.minFrames || sun === null) return {
			ok: false,
			reason: hiddenBecause ?? "no-frames"
		};
		const odoMedian = medianDirection(window.map((s) => s.rOdo));
		const spreadDeg = nearestRankPercentile(window.map((s) => separationDeg(s.rOdo, odoMedian)), rules.spreadPercentile);
		if (spreadDeg > rules.maxSpreadDeg) return {
			ok: false,
			reason: "moved",
			spreadDeg,
			frames: window.length
		};
		const errors = window.map((s) => sightingErrorDeg(s.rGps, s.sun.dir));
		const middle = window[window.length >> 1];
		const first = window[0].yaw;
		const yaws = window.map((s) => first + bearingDeltaDeg$1(s.yaw, first));
		const yawMin = Math.min(...yaws);
		const yawMax = Math.max(...yaws);
		const targets = window.map((s) => s.target);
		const targetChanged = targets.some((x) => x === null || Math.abs(x - (targets[0] ?? 0)) > 1e-9) && targets.some((x) => x !== null);
		const rayXr = nueToWebXR([...odoMedian]);
		const place = sun.place;
		const screenAngle = guarded(() => typeof screen === "undefined" ? 0 : screen.orientation?.angle ?? 0, 0);
		const sighting = {
			schema: 1,
			mode: "reticle",
			atMs: middle.t,
			windowStartMs: window[0].t,
			frames: window.length,
			spreadDeg,
			rayXrX: rayXr[0],
			rayXrY: rayXr[1],
			rayXrZ: rayXr[2],
			viewQx: middle.viewQ.x,
			viewQy: middle.viewQ.y,
			viewQz: middle.viewQ.z,
			viewQw: middle.viewQ.w,
			projP0: middle.projection[0],
			projP5: middle.projection[5],
			projP8: middle.projection[8],
			projP9: middle.projection[9],
			viewportW: middle.viewportW,
			viewportH: middle.viewportH,
			screenAngleDeg: screenAngle,
			displayedYawDeg: normalizeBearingDeg(interpolatingMedian(yaws)),
			yawMinDeg: yawMin,
			yawMaxDeg: yawMax,
			targetYawDeg: middle.target,
			targetChanged,
			lat: place.lat,
			lng: place.lng,
			sunAzDeg: middle.sun.azDeg,
			sunElApparentDeg: middle.sun.elDeg,
			refractionDeg: middle.sun.refractionDeg,
			pressureHPa: air.pressureHPa,
			temperatureC: air.temperatureC,
			clockOffsetMs: deps.nowEpochMs() - monotonic(),
			headingErrDeg: interpolatingMedian(errors.map((e) => e.headingDeg)),
			elevationErrDeg: interpolatingMedian(errors.map((e) => e.elevationDeg)),
			separationDeg: interpolatingMedian(errors.map((e) => e.separationDeg))
		};
		const warnings = [];
		if (middle.sun.elDeg > rules.highSunDeg) warnings.push("high-sun");
		if (targetChanged) warnings.push("target-changed");
		if (yawMax - yawMin > rules.maxYawRangeDeg) warnings.push("alignment-moving");
		return {
			ok: true,
			sighting,
			warnings
		};
	};
	const settle = (result) => {
		if (pending === null) return;
		const { resolve, timer } = pending;
		clearTimeout(timer);
		pending = null;
		resolve(result);
	};
	const finish = (start, end) => settle(guarded(() => aggregate(start, end), {
		ok: false,
		reason: "no-frames"
	}));
	const markNow = () => {
		const hidden = refresh();
		if (hidden !== void 0) return Promise.resolve({
			ok: false,
			reason: hidden
		});
		if (sun !== null && sun.elDeg < rules.minMarkElevationDeg) return Promise.resolve({
			ok: false,
			reason: "sun-down"
		});
		const pressed = deps.nowEpochMs();
		const end = pressed - rules.preGuardMs;
		const start = end - rules.holdMs;
		const before = samples.filter((s) => s.t >= start && s.t <= end);
		if (before.length >= rules.minFrames && before[0].t <= start + .1 * rules.holdMs) {
			const result = aggregate(start, end);
			if (result.ok || result.reason !== "moved") return Promise.resolve(result);
		}
		const after = pressed + rules.joltMs;
		return new Promise((resolve) => {
			const timer = setTimeout(() => finish(after, after + rules.holdMs), rules.joltMs + rules.holdMs + rules.giveUpMs);
			pending = {
				start: after,
				end: after + rules.holdMs,
				resolve,
				timer
			};
		});
	};
	const check = {
		marker,
		status: () => ({
			visible: marker.object.visible,
			...hiddenBecause !== void 0 ? { hiddenBecause } : {},
			...sun !== null ? {
				sunAzDeg: sun.azDeg,
				sunElDeg: sun.elDeg
			} : {}
		}),
		mark() {
			if (disposed) return Promise.resolve({
				ok: false,
				reason: "disposed"
			});
			if (pending !== null) return Promise.resolve({
				ok: false,
				reason: "busy"
			});
			return guarded(markNow, Promise.resolve({
				ok: false,
				reason: "no-frames"
			}));
		},
		dispose() {
			if (disposed) return;
			disposed = true;
			settle({
				ok: false,
				reason: "disposed"
			});
			unregisterFrame();
			unregisterXr();
			deregisterSession();
			marker.dispose();
		}
	};
	const deregisterSession = registerSessionDisposer(() => check.dispose());
	refresh();
	return check;
}
//#endregion
export { SUN_CHECK, startSunCheck };
